import { useMutation } from '@tanstack/react-query'

import { authAPI } from '@/api/new-api'
import { composeFormData } from '@/utils'

import { BookingType } from './booking-types.types'
import { useInvalidateBookingQuestions } from './get-booking-questions'
import { useInvalidateBookingTypes } from './get-booking-types'

export interface BookingTypeFields {
  buildingIds: number[]
  color: string
  description: string
  icon: string
  image?: File
  internalName: string
  link: string
  name: string
}

export interface CreateBookingTypeRequest extends BookingTypeFields {
  category: number
  duplicateId?: number
  order: number
}

export interface UpdateBookingTypeRequest extends BookingTypeFields {
  id: number
}

const toFormData = ({ buildingIds, ...fields }: BookingTypeFields & Record<string, unknown>) =>
  composeFormData({ ...fields, buildings: JSON.stringify(buildingIds) })

// Note: BE reads req.file('image') on every booking type write - always multipart, even without a file
const MULTIPART = { headers: { 'content-type': 'multipart/form-data' } }

export const createBookingType = async (data: CreateBookingTypeRequest) => {
  const response = await authAPI.post<BookingType>(
    '/booking-type',
    toFormData({ ...data, active: true }),
    MULTIPART,
  )
  return response.data
}

export const updateBookingType = async ({ id, ...data }: UpdateBookingTypeRequest) => {
  const response = await authAPI.put<BookingType>(
    `/booking-type/${id}`,
    toFormData(data),
    MULTIPART,
  )
  return response.data
}

export const deleteBookingType = async (id: number) => {
  await authAPI.delete(`/booking-type/${id}`)
}

export const useCreateBookingType = () => {
  const invalidateTypes = useInvalidateBookingTypes()
  const invalidateQuestions = useInvalidateBookingQuestions()

  return useMutation({
    mutationFn: createBookingType,
    onSuccess: (_, { duplicateId }) =>
      Promise.all([invalidateTypes(), duplicateId ? invalidateQuestions() : undefined]),
  })
}

export const useUpdateBookingType = () => {
  const invalidate = useInvalidateBookingTypes()

  return useMutation({
    mutationFn: updateBookingType,
    onSuccess: invalidate,
  })
}

export const useDeleteBookingType = () => {
  const invalidateTypes = useInvalidateBookingTypes()
  const invalidateQuestions = useInvalidateBookingQuestions()

  return useMutation({
    mutationFn: deleteBookingType,
    onSuccess: () => Promise.all([invalidateTypes(), invalidateQuestions()]),
  })
}

export const reorderBookingTypeRequest = async ({ id, order }: { id: number; order: number }) => {
  await authAPI.put(`/booking-type/${id}`, composeFormData({ order }), MULTIPART)
}
